import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { IContentItem } from "@kontent-ai/delivery-sdk"

import type { BlockText } from "@/types/kontent-generated/types/block-text.generated"
import type { Button as SDKButton } from "@/types/kontent-generated/types/button.generated"
import type { TextBlocks } from "@/types/kontent-generated/types/text-blocks.generated"
import { IKTRichText } from "@/types/kontent.types"
import { getDeliveryClient } from "@/lib/kontent/client"
import { getWebSpotlightApplicationTree } from "@/lib/kontent/config/getApplicationTree"
import { TButton } from "@/components/ui/atoms"

import { assertItemResponse, fetchKontent } from "../helpers"
import {
  mapButtonToTButton,
  mapRichTextElementToIKTRichText,
  parseCustomElementJsonValue,
} from "../mappers"
import { resolveRichText } from "../richText"

type TResolvedBlockTextRemixIcon = {
  title: string
  icon: string
  body: IKTRichText
}

export type TResolvedTextBlocks = {
  title: string
  body: IKTRichText
  smallInfo?: string
  button: TButton[]
  style: "Feature List" | "Card Feature List" | "Text and Card List"
  textBlocks: TResolvedBlockTextRemixIcon[]
}

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function GetTextBlocksFromItem(
  rawItem: IContentItem,
  previewMode = false
): Promise<TResolvedTextBlocks> {
  const item = rawItem as TextBlocks

  let body: IKTRichText = mapRichTextElementToIKTRichText(item.elements.body)
  body = await resolveRichText(body, previewMode)

  const textBlocks = await Promise.all(
    (item.elements.text_blocks.linkedItems as BlockText[]).map(
      async (block) => {
        let blockBody = mapRichTextElementToIKTRichText(block.elements.body)
        blockBody = await resolveRichText(blockBody, previewMode)
        return {
          title: block.elements.title.value,
          icon:
            parseCustomElementJsonValue(block.elements.icon_selector.value) ??
            "",
          body: blockBody,
        }
      }
    )
  )

  const { byCodename } = await getWebSpotlightApplicationTree(previewMode)
  const buttons = (item.elements.button.linkedItems as SDKButton[]).map((btn) =>
    mapButtonToTButton(btn, byCodename)
  )

  return {
    title: item.elements.title.value,
    body,
    style: item.elements.style.value[0]?.name as TResolvedTextBlocks["style"],
    smallInfo: item.elements.small_info.value,
    textBlocks,
    button: buttons,
  }
}

async function _fetchTextBlocks(
  codename: string,
  previewMode = false
): Promise<TResolvedTextBlocks> {
  return fetchKontent("TextBlocks", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<TextBlocks>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(2)
      .toPromise()

    const item = assertItemResponse(response, "TextBlocks")
    return GetTextBlocksFromItem(item, previewMode)
  })
}

export const GetTextBlocks = cache(_fetchTextBlocks)
